import { Input, type InputProps } from '@components/ui/input';
import { cn } from '@lib/utils';
import { useStore } from '@tanstack/react-form';
import { useEffect, useRef } from 'react';
import { useFieldContext, useFormContext } from './form';
import { FormDescription, FormErrorMessage, FormField, FormLabel } from './shared.component';

type FileFieldProps = Omit<InputProps, 'type' | 'value'> & {
  className?: string;
  description?: string;
  inputClassName?: string;
  label?: string;
  tooltip?: React.ReactNode;
};

export function FileField(props: FileFieldProps) {
  const field = useFieldContext<File | null | undefined>();
  const form = useFormContext();
  const { className, description, inputClassName, label, required, tooltip, onChange, ...otherProps } = props;
  const inputRef = useRef<HTMLInputElement>(null);

  const isSubmitting = useStore(form.store, (state) => state.isSubmitting);

  useEffect(() => {
    if (field.state.value || !inputRef.current) {
      return;
    }

    inputRef.current.value = '';
  }, [field.state.value]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) return onChange(event);

    const file = event.target.files?.[0] ?? null;
    field.handleChange(file);
  };

  return (
    <FormField className={cn('w-full', className)}>
      {label && (
        <FormLabel
          htmlFor={field.name}
          required={required}
          tooltip={tooltip}
        >
          {label}
        </FormLabel>
      )}
      {description && <FormDescription>{description}</FormDescription>}
      <Input
        aria-invalid={!field.state.meta.isValid}
        className={cn('w-full', inputClassName)}
        disabled={isSubmitting}
        id={field.name}
        name={field.name}
        ref={inputRef}
        onBlur={field.handleBlur}
        hasError={!field.state.meta.isValid}
        onChange={handleChange}
        type="file"
        {...otherProps}
      />
      <FormErrorMessage />
    </FormField>
  );
}
